import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/cn'
import styles from './Pagination.module.scss'

export interface PaginationProps {
  /** 1-based current page. */
  page: number
  /** Items per page (typically 25/50/100). */
  pageSize: number
  /** Total items across all pages. */
  total: number
  /** Page sizes the user can pick. Defaults to 25/50/100. */
  pageSizeOptions?: ReadonlyArray<number>
  onPageChange: (next: number) => void
  onPageSizeChange?: (next: number) => void
  /** Singular noun for the summary line ("vídeo", "upload"). */
  itemLabel?: string
  /** Plural override (else appends 's'). */
  itemLabelPlural?: string
}

/**
 * Numbered pagination matching the Prism design's ``.paginator`` (see
 * ``ranking.html``): summary on the left, page-size select inline,
 * prev/next + numbered buttons on the right with ellipsis when there are
 * many pages.
 *
 * Stateless — caller owns ``page`` / ``pageSize`` and gets called back
 * via ``onPageChange`` / ``onPageSizeChange`` so it can persist to URL or
 * to a Zustand store.
 */
export function Pagination({
  page,
  pageSize,
  total,
  pageSizeOptions = [25, 50, 100],
  onPageChange,
  onPageSizeChange,
  itemLabel = 'item',
  itemLabelPlural,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.max(1, Math.min(page, totalPages))
  const startIdx = total === 0 ? 0 : (safePage - 1) * pageSize + 1
  const endIdx = Math.min(safePage * pageSize, total)
  const noun = total === 1 ? itemLabel : itemLabelPlural ?? `${itemLabel}s`
  // Hide the prev/next/numbered nav when there's only one page worth of
  // data — the buttons would all be disabled and add visual noise. We
  // still render the summary line + page-size selector so the user can
  // shrink the page (which might create more pages and bring the nav
  // back) or just see the "X de Y" count.
  const showNav = totalPages > 1
  const numbers = showNav ? computePageWindow(safePage, totalPages) : []

  return (
    <div className={styles.shell}>
      <div className={styles.summary}>
        <span>
          Mostrando <span className={styles.summaryHl}>{startIdx}–{endIdx}</span>{' '}
          de <span className={styles.summaryHl}>{total}</span> {noun}
        </span>
        {onPageSizeChange ? (
          <span className={styles.sizeSelect}>
            ·{' '}
            <select
              className={styles.sizeSelectInput}
              value={pageSize}
              aria-label="Itens por página"
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value))
              }}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt} por página
                </option>
              ))}
            </select>
          </span>
        ) : null}
      </div>

      {showNav ? (
      <div className={styles.nums}>
        <button
          type="button"
          className={styles.btn}
          aria-label="Página anterior"
          disabled={safePage <= 1}
          onClick={() => {
            onPageChange(safePage - 1)
          }}
        >
          <ChevronLeft />
        </button>
        {numbers.map((entry, i) => (
          entry === 'ellipsis' ? (
            <span key={`ell-${String(i)}`} className={styles.ellipsis} aria-hidden>
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              className={cn(styles.btn, entry === safePage && styles.active)}
              aria-current={entry === safePage ? 'page' : undefined}
              aria-label={`Ir para a página ${String(entry)}`}
              onClick={() => {
                onPageChange(entry)
              }}
            >
              {entry}
            </button>
          )
        ))}
        <button
          type="button"
          className={styles.btn}
          aria-label="Próxima página"
          disabled={safePage >= totalPages}
          onClick={() => {
            onPageChange(safePage + 1)
          }}
        >
          <ChevronRight />
        </button>
      </div>
      ) : null}
    </div>
  )
}

/**
 * Compact page window: always show first/last; show 1 neighbour on each
 * side of current; collapse the rest with ellipsis. Examples:
 *   1 page → [1]
 *   3 pages, current=2 → [1,2,3]
 *   10 pages, current=1 → [1,2,3,'…',10]
 *   10 pages, current=5 → [1,'…',4,5,6,'…',10]
 *   10 pages, current=10 → [1,'…',8,9,10]
 */
function computePageWindow(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const out: Array<number | 'ellipsis'> = [1]
  const left = Math.max(2, current - 1)
  const right = Math.min(total - 1, current + 1)

  if (left > 2) out.push('ellipsis')
  for (let i = left; i <= right; i++) out.push(i)
  if (right < total - 1) out.push('ellipsis')

  out.push(total)
  return out
}
